/**
 * Estrutura organizacional oficial da COOASGO (setores, seções e centros de
 * custo). Fonte: relatório "Rel. Centro de Custos" (Infogen) e planilha de
 * Filial/Setor/Seção — 12/01/2023.
 *
 * Objetivo: padronizar o preenchimento dos relatórios de despesa. O usuário
 * seleciona (com busca) em vez de digitar, evitando a despadronização que
 * quebra o agrupamento por Setor / Seção / Centro de Custo.
 *
 * Para atualizar: edite as listas abaixo. Os valores gravados são as strings
 * de `sector`, `section` e `cost_center` — mantenha-as consistentes com o que
 * já existe em banco quando fizer alterações.
 */

export interface IOrgSection {
  // Código da seção (ex: "103", "901"). É o valor gravado em `section`.
  code: string;
  // Nome descritivo da seção.
  name: string;
  // Código do setor ao qual a seção pertence (ex: "1", "99").
  sector: string;
}

export interface IOrgSector {
  // Código do setor (ex: "1", "4", "99"). É o valor gravado em `sector`.
  code: string;
  // Nome de referência do setor (quando conhecido).
  name?: string;
}

export interface ICostCenter {
  // Código do centro de custo (ex: "3092"). É o valor gravado em `cost_center`.
  code: string;
  // Nome descritivo do centro de custo.
  name: string;
}

// ---------------------------------------------------------------------------
// SETORES × SEÇÕES (Filial → Setor → Seção). Cada seção referencia seu setor.
// Consolidado de todas as filiais; setores/seções repetidos entre filiais
// aparecem uma única vez (o par setor+seção é único).
// ---------------------------------------------------------------------------

export const ORG_SECTIONS: IOrgSection[] = [
  // MATRIZ (filial 1)
  { code: '103', name: 'Operacional Cereais', sector: '1' },
  { code: '901', name: 'Técnico Veterinário/Ambiental', sector: '9' },
  { code: '402', name: 'Fábrica Bovina', sector: '4' },
  { code: '404', name: 'ADM Fábrica', sector: '4' },
  { code: '501', name: 'Agrícola', sector: '5' },
  { code: '901', name: 'Conselho de Administração', sector: '99' },
  { code: '902', name: 'Conselho Vogal', sector: '99' },
  { code: '903', name: 'Conselho Fiscal', sector: '99' },
  { code: '904', name: 'ADM Central', sector: '99' },
  { code: '908', name: 'Manutenção', sector: '99' },
  { code: '910', name: 'Segurança e Med. Trabalho/SESMT', sector: '99' },
  { code: '911', name: 'T.I', sector: '99' },
  { code: '913', name: 'Diretoria Executiva', sector: '99' },
  { code: '914', name: 'Controladoria', sector: '99' },
  { code: '915', name: 'Gestão Ambiental', sector: '99' },
  { code: '916', name: 'Comunicação e Social', sector: '99' },
  { code: '917', name: 'Logística', sector: '99' },

  // UPL I (filial 2)
  { code: '201', name: 'ADM UPL', sector: '2' },
  { code: '203', name: 'Maternidade', sector: '2' },
  { code: '209', name: 'Creche', sector: '2' },
  { code: '205', name: 'Recria', sector: '2' },

  // GRANJA PONTO ALTO (filial 4)
  { code: '204', name: 'Creche', sector: '2' },
  { code: '206', name: 'Terminação', sector: '2' },

  // VETERINARIA (filial 5)
  { code: '502', name: 'Veterinária', sector: '5' },
  { code: '912', name: 'Expedição', sector: '99' },

  // MERCADO - SUPER COOASGO RV (filial 6)
  { code: '515', name: 'Mercado/Área de Vendas', sector: '5' },
  { code: '516', name: 'Administrativo', sector: '5' },
  { code: '517', name: 'Frente de Caixa', sector: '5' },
  { code: '519', name: 'FLV - Frutas, Leg. e Verduras', sector: '5' },
  { code: '520', name: 'FLC - Frios Lácteos e Cong.', sector: '5' },
  { code: '521', name: 'Açougue', sector: '5' },
  { code: '522', name: 'Padaria', sector: '5' },
  { code: '523', name: 'Armazenagem', sector: '5' },

  // UDG - CENTRAL SEMEN (filial 7) - Desativada
  { code: '801', name: 'UDG', sector: '8' },

  // RONDONOPOLIS (filial 9)
  { code: '302', name: 'Rondonópolis', sector: '3' },

  // GRANJA RIO VERDE (filial 10)
  { code: '202', name: 'Gestação', sector: '2' },
  { code: '208', name: 'Creche', sector: '2' },

  // CEREALISTA (filial 11)
  // seção 103 / setor 1 já cadastrada

  // CENTRO DISTRIBUIÇÃO COXIM (filial 13)
  // seção 912 / setor 99 já cadastrada

  // NOVA FABRICA RACAO SUINA (filial 15)
  { code: '401', name: 'Fábrica Suína', sector: '4' },

  // CENTRO ADM E FINANCEIRO (filial 19)
  { code: '905', name: 'Contabilidade', sector: '99' },
  { code: '906', name: 'Financeiro', sector: '99' },
  { code: '909', name: 'Recursos Humanos', sector: '99' },
  { code: '907', name: 'Suprimentos', sector: '99' },
  // seção 302 / setor 3 (Suínos Abate) já cadastrada
];

// Setores distintos derivados das seções, com nome de referência.
export const ORG_SECTORS: IOrgSector[] = [
  { code: '1', name: 'Operacional / Cereais' },
  { code: '2', name: 'UPL / Granjas' },
  { code: '3', name: 'Suínos / Abate' },
  { code: '4', name: 'Fábrica' },
  { code: '5', name: 'Agrícola / Mercado / Veterinária' },
  { code: '8', name: 'UDG' },
  { code: '9', name: 'Técnico Veterinário/Ambiental' },
  { code: '99', name: 'Administrativo / Corporativo' },
];

// ---------------------------------------------------------------------------
// CENTROS DE CUSTO (lista independente). Fonte: Rel. Centro de Custos.
// ---------------------------------------------------------------------------

export const COST_CENTERS: ICostCenter[] = [
  { code: '1', name: 'Sem Centro de Custo' },
  { code: '1116', name: 'Adiantamento Férias' },
  { code: '1157', name: 'Estoque Produção Própria' },
  { code: '1287', name: 'Prêmio de Seguro - Ativo' },
  { code: '1289', name: 'Aluguéis' },
  { code: '1290', name: 'Vale Alimentação / Sem Rateio' },
  { code: '1360', name: 'Participação Societ. / Sicredi' },
  { code: '1380', name: 'Terrenos e Terras' },
  { code: '1381', name: 'Imobilizado Edif. e Construções' },
  { code: '1382', name: 'Imobilizado Veículos' },
  { code: '1383', name: 'Imobilizado Maq. Equip. Indust' },
  { code: '1384', name: 'Imobilizado Maq. e Equipamento' },
  { code: '1385', name: 'Imobilizado Equip. de Informat' },
  { code: '1386', name: 'Imobilizado Equip. Laboratorio' },
  { code: '1387', name: 'Imobilizado Equip. de Prot. Seg.' },
  { code: '1388', name: 'Imobilizado Moveis e Utensilio' },
  { code: '1389', name: 'Imobilizado Instalacoes' },
  { code: '1424', name: 'Reprodutores Suínos Fêmeas' },
  { code: '1425', name: 'Reprodut. Suinos Machos' },
  { code: '1426', name: 'Animais Reprofutores Equinos' },
  { code: '1429', name: 'Imobilizado Florest. e Reflor.' },
  { code: '1436', name: 'Const. Andam / Mar Construção' },
  { code: '1437', name: 'Constr. Andamento Outras' },
  { code: '1438', name: 'Constr. Andamento Mão de Obra' },
  { code: '1439', name: 'Constr. Andamento / Impostos' },
  { code: '1440', name: 'Constr. Andamento / Enc. Financ.' },
  { code: '1441', name: 'Constr. Andamento / Maq e Equipa' },
  { code: '1442', name: 'Constr Andamento / Software' },
  { code: '1447', name: 'Reformas Andamento Impostos' },
  { code: '1448', name: 'Constr. Andamento Enc. Financei' },
  { code: '1454', name: 'Consórcios N. Contemplado' },
  { code: '1461', name: 'Veiculos Destinados a Venda' },
  { code: '1494', name: 'Indenização Seguro a Receber' },
  { code: '1510', name: 'Mão de Obra c/ Encargos' },
  { code: '1530', name: 'At. Biol. em Andamento' },
  { code: '1550', name: 'Animais em Comodato - Fêmeas' },
  { code: '2005', name: 'Fornecedor a Pagar' },
  { code: '2035', name: 'Banco do Brasil (Pesa) Juros' },
  { code: '2149', name: 'Consórcio - Carta de Crédito' },
  { code: '2152', name: 'Consignações' },
  { code: '3027', name: '*Não Usar*' },
  { code: '3029', name: 'Alimentação e Medicação Anim.' },
  { code: '3032', name: 'Desinfetantes/Aces Veterinário' },
  { code: '3033', name: 'Material Laboratório/Insemin.' },
  { code: '3035', name: 'Royalties Reprod. Suinos' },
  { code: '3036', name: 'Semen Consumido' },
  { code: '3037', name: 'Lenha Secador Agrícola' },
  { code: '3038', name: 'Medicamentos Suicooper - Misto' },
  { code: '3066', name: 'Assist Médica / Plano de Saude' },
  { code: '3068', name: 'Programa de Alimentação Trabal' },
  { code: '3069', name: 'Uniformes' },
  { code: '3070', name: 'Seguro de Vida' },
  { code: '3071', name: 'Equipamento Proteção Individua' },
  { code: '3073', name: 'Transporte Funcionários' },
  { code: '3077', name: 'Aluguéis de Software' },
  { code: '3078', name: 'Aluguéis e Arrendamento' },
  { code: '3080', name: 'Área Experimental' },
  { code: '3081', name: 'Bens de Duração Temporária' },
  { code: '3082', name: 'Brindes e Doações Suicooper' },
  { code: '3083', name: 'Brindes e Doações' },
  { code: '3084', name: 'Cédula de Presença' },
  { code: '3085', name: 'Combustivel e Lubrificante' },
  { code: '3087', name: 'Confraternizações' },
  { code: '3088', name: 'Contrib. Entidade de Classe' },
  { code: '3089', name: 'Contrib Sistema Cooperativista' },
  { code: '3090', name: 'Correios e Malotes' },
  { code: '3092', name: 'Energia Eletrica' },
  { code: '3093', name: 'Fretes e Carretos / Despesa' },
  { code: '3094', name: 'Higiene/Limpeza e Copa/Cozinha' },
  { code: '3095', name: 'Incentivo a Produtividade Func' },
  { code: '3096', name: 'Indenizaçao Judicial / Diversas' },
  { code: '3097', name: 'Indenização Processo Trabalhis' },
  { code: '3098', name: 'Internet' },
  { code: '3099', name: 'Licenciamento de Veículos' },
  { code: '3100', name: 'Material de Expediente' },
  { code: '3101', name: 'Material Laboratório/Diversos' },
  { code: '3102', name: 'Outros Mat. de Uso e Consumo' },
  { code: '3103', name: 'Pedágios Rodoviarios' },
  { code: '3104', name: 'Produtos p/Trat. de Armazéns' },
  { code: '3105', name: 'Program de Radio/Assin Revist.' },
  { code: '3106', name: 'Propaganda e Publicidade' },
  { code: '3107', name: 'Quilometragem' },
  { code: '3108', name: 'Refeições e Lanches' },
  { code: '3109', name: 'Registro e Auten. Cartórios' },
  { code: '3110', name: 'Segurança e Medicina do Trabal' },
  { code: '3111', name: 'Seguro s/ Patrimonio' },
  { code: '3113', name: 'Taxas Bancarias' },
  { code: '3115', name: 'Telefone' },
  { code: '3116', name: 'Viagens e Estadias' },
  { code: '3117', name: 'Gastos c/ Informatica' },
  { code: '3118', name: 'Outras Despesas Operacionais' },
  { code: '3120', name: 'Serviços de Asses e Cons Ambie' },
  { code: '3121', name: 'Serviços Terceiros P. Juridica' },
  { code: '3122', name: 'Serviços de Pessoa Física' },
  { code: '3123', name: 'Serviços Limpeza, Jard e Cons.' },
  { code: '3124', name: 'Serviços de Asses Informatica' },
  { code: '3125', name: 'Serviços de Asses Juridica' },
  { code: '3126', name: 'Serviços de Engenharia Civil' },
  { code: '3127', name: 'Serviços Medico de Trabalho' },
  { code: '3128', name: 'Serviços de Detetização' },
  { code: '3129', name: 'Serviços de Auditoria / Consult' },
  { code: '3130', name: 'Serviço Assessoria de Comunica' },
  { code: '3131', name: 'Serviços Vigilância Segurança' },
  { code: '3132', name: 'Medicamentos/Aces. Parc. Campo' },
  { code: '3136', name: 'Multas Punitivas' },
  { code: '3147', name: 'Curso e Treinamento' },
  { code: '3148', name: 'Assistencia Social - Fates' },
  { code: '3151', name: 'Manutenção Edifícios e Constru' },
  { code: '3152', name: 'Manutenção de Máquinas e Equip' },
  { code: '3153', name: 'Manutenção Equip/Informatica' },
  { code: '3154', name: 'Manutenção de Veículos' },
  { code: '3155', name: 'Manutenção de Móveis, Utensíli' },
  { code: '3161', name: 'IPVA' },
  { code: '3163', name: 'ITR' },
  { code: '3165', name: 'Impostos e Taxas Municipais' },
  { code: '3166', name: 'Impostos e Taxas Estaduais' },
  { code: '3167', name: 'Impostos e Taxas Federais' },
  { code: '3179', name: 'Comissões Sobre Vendas' },
  { code: '3180', name: 'Fretes s/ Vendas' },
  { code: '3187', name: 'Recuperação de Dispendios' },
  { code: '3191', name: 'Bonificações Recebidas' },
  { code: '3196', name: 'Dispendios Eventos Promocionais' },
  { code: '3201', name: 'Dispendios Oper. com Associados' },
  { code: '3204', name: 'Alienação de Bens Imobilizado' },
  { code: '3205', name: 'Alienação Ativos Biológicos' },
  { code: '3207', name: 'Custo de Bens Alienados' },
  { code: '3209', name: 'Venda Ativo Biologico' },
  { code: '3237', name: 'Juros Pagos' },
  { code: '3239', name: 'Descontos Concedidos' },
  { code: '3381', name: 'Servico Cobrança' },
  { code: '3775', name: 'Ensino Superior e Especializ.' },
  { code: '3776', name: 'Fretes de Venda / Suicooper' },
  { code: '3777', name: 'Código Vago' },
  { code: '3778', name: 'Cod Vago' },
  { code: '3799', name: 'Alienação Sucatas' },
  { code: '3808', name: 'Recuperação Despesa Royalties' },
  { code: '3809', name: 'Indenizações de Seguros' },
  { code: '3861', name: 'Doações Pandemia' },
  { code: '3874', name: 'Parcerias de Vendas' },
  { code: '3880', name: 'Medicamentos Suicooper - UPL I' },
  { code: '3897', name: 'Manutenção Pátio' },
  { code: '5031', name: 'Assist Médica / Plano de Saude' },
  { code: '5032', name: 'Auxílio Educação' },
  { code: '5033', name: 'Programa de Alimentação Trabal' },
  { code: '5034', name: 'Uniformes' },
  { code: '5035', name: 'Seguro de Vida' },
  { code: '5036', name: 'Equipamento Prot. Ind.' },
  { code: '5037', name: 'Treinamento Funcionários' },
  { code: '5038', name: 'Transporte Funcionários' },
  { code: '5041', name: 'Energia Elétrica' },
  { code: '5044', name: 'Combustiveis e Lubrificantes' },
  { code: '5045', name: 'Fretes / Carretos - Custo Prod.' },
  { code: '5046', name: 'Aliment. e Med. de Animais' },
  { code: '5047', name: 'Outros Mater. de Uso e Consumo' },
  { code: '5048', name: 'Refeições e Lanches' },
  { code: '5049', name: 'Outras Despesas Operac.' },
  { code: '5050', name: 'Desinfetantes e Acesso. Veter.' },
  { code: '5051', name: 'Material de Labor e Insem' },
  { code: '5052', name: 'Royalties Reprodutores Suinos' },
  { code: '5054', name: 'Serviços Terceiros PJ' },
  { code: '5055', name: 'Serviço de Pessoa Física' },
  { code: '5056', name: 'Serviço Medico do Trabalho' },
  { code: '5057', name: 'Servico Detetizacao' },
  { code: '5059', name: 'Depreciação' },
  { code: '5064', name: 'Manut. Edif. e Contruções' },
  { code: '5065', name: 'Manut. Máquinas e Equip.' },
  { code: '5066', name: 'Manutenção de Veículos' },
  { code: '5067', name: 'Manutenção de Mov. Utens. e In' },
  { code: '5089', name: 'Serviço de Limpeza e Cons.' },
  { code: '5090', name: 'Segurança e Med. do Trab.' },
];

// Seções pertencentes a um setor (encadeamento Setor → Seção).
export function getSectionsBySector(sectorCode: string): IOrgSection[] {
  return ORG_SECTIONS.filter(s => s.sector === sectorCode);
}
